import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Breadcrumb, Card, Button, Typography, Space } from 'antd';
import { ArrowLeftOutlined, DownOutlined } from '@ant-design/icons';
import { api } from '../api';
import { t } from '../i18n';
import { fmtDateTime } from '../time';

function fmtTs(unix: number): string {
  return fmtDateTime(unix);
}

/** Color a log line by content: device-reported failures/errors in red,
 * warnings in gold, everything else (normal narrative events) unstyled. */
function lineColor(message: string): string | undefined {
  const m = message.toLowerCase();
  if (m.includes('fail') || m.includes('error') || m.includes('cannot') || m.includes('reject')) return '#c9302c';
  if (m.includes('warn')) return '#b58900';
  return undefined;
}

const PAGE = 100;

/** Full-page device log, replacing the earlier modal (too small to show
 * even one full line). Lazily paginated — loads the newest page first,
 * "Load more" reaches further back rather than shipping the whole
 * per-device history up front. */
export function DeviceLogPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<Array<{ id: number; ts: number; message: string }>>([]);
  const [hasMore, setHasMore] = useState(true);

  const devicesQ = useQuery({ queryKey: ['devices'], queryFn: api.listDevices });
  const device = devicesQ.data?.find((d) => d.id === id);

  // Reset pagination if navigated straight from one device's log to another.
  useEffect(() => {
    setOffset(0);
    setItems([]);
    setHasMore(true);
  }, [id]);

  // Fetch PAGE+1 as a look-ahead probe: if the server returns more than PAGE we
  // know another page exists, so "Load more" is only shown when there truly is
  // more — no off-by-one empty fetch on exact multiples of PAGE.
  const logsQ = useQuery({
    queryKey: ['device-logs', id, offset],
    queryFn: () => api.getDeviceLogs(id!, { limit: PAGE + 1, offset }),
    enabled: !!id,
  });
  useEffect(() => {
    if (!logsQ.data) return;
    const got = logsQ.data.items;
    const pageItems = got.slice(0, PAGE); // drop the +1 probe before displaying
    setItems((prev) => (offset === 0 ? pageItems : [...prev, ...pageItems]));
    setHasMore(got.length > PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logsQ.dataUpdatedAt]);

  const lines = [...items].reverse();
  const label = device?.label || id?.substring(0, 8) || '';

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/devices">{t('devicesTitle')}</Link> },
          { title: label },
          { title: t('deviceShowLog') },
        ]}
      />
      <Card
        title={t('deviceLogDialogTitle', { label })}
        extra={<Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/devices')}>{t('dialogClose')}</Button>}
      >
        {logsQ.isLoading && offset === 0 ? null : lines.length === 0 ? (
          <Typography.Text type="secondary">{t('deviceNoLogs')}</Typography.Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <div style={{ textAlign: 'center' }}>
              <Button
                size="small"
                icon={<DownOutlined />}
                loading={logsQ.isFetching}
                disabled={!hasMore}
                onClick={() => setOffset((o) => o + PAGE)}
              >
                {hasMore ? t('deviceLogLoadMore') : t('deviceLogAllLoaded')}
              </Button>
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {lines.map((l) => (
                <div key={l.id}>
                  <span style={{ color: '#6b7280' }}>{fmtTs(l.ts)}</span>
                  {' — '}
                  <span style={{ color: lineColor(l.message) }}>{l.message}</span>
                </div>
              ))}
            </div>
          </Space>
        )}
      </Card>
    </div>
  );
}
